import { useMemo } from 'react';

import { BillingSubscriptionStatus } from '@sandtech-hub-monorepo/ehub-client';
import { TimeUnit } from '@sandtech-hub-monorepo/ui-kit';

import { useStripeSubscriptions } from './useStripeSubscriptions';

/*
 * TODO(PEDS-XXXX): have the server say this rather than restating it here.
 * The three constants below — ENTITLING, SLOT_HOLDING and SLOT_GRACE_MS —
 * mirror ENTITLING_STATUSES, SLOT_HOLDING_STATUSES and SLOT_GRACE_MS in
 * apps/account/src/graphql/billing/billing-coverage.ts, and nothing links the
 * two files. When the server's rule changes, this one goes quietly wrong: a
 * learner is shown unlocked content the server would refuse, or is pointed at
 * Settings for a plan the server would have sold them.
 *
 * Two derived booleans on LearnerStripeSubscription — `entitling` and
 * `holdsSlot`, resolved from the constants that already exist — would delete
 * all three of these plus the currentPeriodEnd comparison below, and leave
 * this hook a pure fold over what the server sent. It is the only version of
 * this that cannot rot.
 */

/**
 * Statuses that grant access, mirroring ENTITLING_STATUSES on the server.
 * PAST_DUE is in it deliberately: Stripe is still retrying, and pulling access
 * mid-dunning punishes a learner whose card will probably work next attempt.
 */
const ENTITLING: BillingSubscriptionStatus[] = [
  BillingSubscriptionStatus.Trialing,
  BillingSubscriptionStatus.Active,
  BillingSubscriptionStatus.PastDue,
];

/**
 * Statuses that occupy the one-subscription-per-plan slot, so that sending the
 * learner to the plans page would dead-end at "There is already a subscription
 * for this plan on your account".
 *
 * This mirrors SLOT_HOLDING_STATUSES in the account subgraph's
 * billing-coverage.ts, and it mirrors it LOOSELY in one direction on purpose
 * and one by necessity. The server also requires currentPeriodEnd to be within
 * SLOT_GRACE_MS, which is applied below — a wedged row must not refuse the
 * plan forever. The server additionally scopes the check to one planId or
 * family, which the client cannot do without knowing which plan is being
 * bought, so this is account-wide and errs towards Settings. With a flat
 * catalogue that difference is unobservable; with a ladder it would show as a
 * learner being pointed at Settings for a plan the server would have sold
 * them.
 */
const SLOT_HOLDING: BillingSubscriptionStatus[] = [
  ...ENTITLING,
  BillingSubscriptionStatus.Incomplete,
  BillingSubscriptionStatus.Unpaid,
  BillingSubscriptionStatus.Paused,
];

/**
 * What the learner's billing looks like account-wide. Not what any one program
 * costs them — that is a join and lives on the enrollment.
 */
export type SubscriptionState =
  /** Never subscribed. */
  | 'none'
  /** Paid up and renewing. Nothing to say. */
  | 'active'
  /** Cancelled, still inside the paid period. */
  | 'expiring'
  /** Payment failed; access continues while Stripe retries. */
  | 'past-due'
  /**
   * The very first payment never completed, so access never started. Separated
   * from `blocked` because it is the one slot-holding status that has no
   * access to lose, and telling this learner anything expired would be false.
   */
  | 'payment-failed'
  /** Had one, it is over, and a new one can be bought. */
  | 'ended'
  /** Not entitling, but still holds the slot, so buying again is refused. */
  | 'blocked';

interface UseSubscriptionStateResult {
  state: SubscriptionState;
  /** Whole days until the live subscription's period ends, floored at zero. */
  daysRemaining: number | null;
  loading: boolean;
}

/** Matches SLOT_GRACE_MS in the account subgraph's billing-coverage.ts. */
const SLOT_GRACE_MS = 7 * TimeUnit.DAY;

export const useSubscriptionState = (): UseSubscriptionStateResult => {
  const { subscriptions, loading } = useStripeSubscriptions();

  return useMemo(() => {
    const now = Date.now();

    // The same test the server's coverage join makes: an entitling status is
    // not enough on its own, the paid period has to still be running.
    const live = subscriptions.find(
      (subscription) =>
        ENTITLING.includes(subscription.status) &&
        new Date(subscription.currentPeriodEnd).getTime() > now
    );

    const daysRemaining = live
      ? Math.max(
          0,
          Math.floor(
            (new Date(live.currentPeriodEnd).getTime() - now) / TimeUnit.DAY
          )
        )
      : null;

    const state = ((): SubscriptionState => {
      // Ahead of `expiring`: a cancelled subscription whose card is also
      // failing needs the card fixed first, or there is nothing to keep.
      if (live?.status === BillingSubscriptionStatus.PastDue) return 'past-due';
      if (live?.cancelAtPeriodEnd) return 'expiring';
      if (live) return 'active';

      /*
       * Ahead of the generic slot check, because INCOMPLETE is the only
       * slot-holding status that never entitled: the first invoice was not
       * paid, usually a declined card or an abandoned 3DS step, so the
       * subscription exists without ever having granted anything. Folded into
       * `blocked` it produced an "Access Expired" label for a learner who had
       * never had access.
       */
      const firstPaymentFailed = subscriptions.some(
        (s) =>
          s.status === BillingSubscriptionStatus.Incomplete &&
          new Date(s.currentPeriodEnd).getTime() > now - SLOT_GRACE_MS
      );

      if (firstPaymentFailed) return 'payment-failed';

      const slotHolder = subscriptions.some(
        (s) =>
          SLOT_HOLDING.includes(s.status) &&
          new Date(s.currentPeriodEnd).getTime() > now - SLOT_GRACE_MS
      );

      if (slotHolder) return 'blocked';

      /*
       * `ended` asserts the learner once had access, so a history of nothing
       * but failed first payments is not it. Stripe expires an unpaid
       * INCOMPLETE subscription into INCOMPLETE_EXPIRED, which holds no slot —
       * without this, that learner was told their subscription had ended when
       * it had never started, and one they could buy again anyway.
       */
      const everEntitled = subscriptions.some(
        (s) =>
          s.status !== BillingSubscriptionStatus.Incomplete &&
          s.status !== BillingSubscriptionStatus.IncompleteExpired
      );

      return everEntitled ? 'ended' : 'none';
    })();

    return { state, daysRemaining, loading };
  }, [subscriptions, loading]);
};
